import styled from "@emotion/styled";
import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons/faBadgeCheck';
import { faTrophyStar } from '@fortawesome/pro-regular-svg-icons/faTrophyStar';
import { faShieldQuartered } from "@fortawesome/pro-solid-svg-icons/faShieldQuartered";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { AppImageDimension } from "app/appImages";
import { MprcRequestErrorDialog, MprcRequestIndicator, MprcText } from "components";
import { MprcProgress } from 'components/MprcProgress';
import { ChallengeMode } from "generated/CoreApi_v1";
import { useSnackbar } from 'notistack';
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useInstanceContext } from 'views/assessment/player/AssPlayerBase';

const ProgressTable = styled("table")(
    ({ theme }) => ({
        borderCollapse: "collapse",
        width: "100%"
    })
);

const ProgressMarker = styled("td")(
    ({ theme }) => ({
        width: "25%",
        height: "10px",
        fontsize: "16px",
    })
);

/////////////////////////////////////////////////////////////////////////////////

export const ChallengeConclusion: React.FC = () => {

    const ctx = useInstanceContext();
    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();

    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.assPlayer.getChallengeResult({ instanceId: ctx.viewData!.instanceId! });
                ctx.setChallengeRev(result.data);
                if (result.data.challengeMode == ChallengeMode.Default && result.data.scorePercent == 100 && result.data.hasProvenKnowledgeSeal) {
                    enqueueSnackbar(t("AssPlayer.Conclusion.Challenge.GrantKnowledgeSealMessage", { name: result.data.topicName }), { variant: "success" });
                }
            } catch (e) {
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    return (
        <>
            <MprcRequestIndicator type="request" visible={loading} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {
                ctx.challengeRev && !loading &&
                <Stack direction="column" spacing={2} mt={1}>
                    <Grid container justifyContent="space-between">
                        <Grid size={{ xs: 3 }}>
                            <Stack direction="column" alignItems="center">
                                <Box mb={0.5}>
                                    <FontAwesomeIcon
                                        icon={faBadgeCheck}
                                        color={theme.palette.success.main}
                                        size="6x"
                                    />
                                </Box>
                                <Typography variant="h5" color="muted.main">
                                    <strong>{ctx.challengeRev.scorePercent}%</strong>
                                </Typography>
                                <Typography variant="h5" color="muted.main" textTransform="uppercase">
                                    {t("AssPlayer.Conclusion.Challenge.CorrectAnswersText")}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid size="grow">
                            <Stack direction="column" alignItems="center">
                                <Typography variant="h2" component="p">
                                    {t("AssPlayer.Conclusion.Challenge.PerformanceTextL1")}
                                </Typography>
                                <Typography variant="h2" component="p">
                                    {t("AssPlayer.Conclusion.Challenge.PerformanceTextL2")}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            {
                                ctx.challengeRev.awardPoints > 0 &&
                                <Stack direction="column" alignItems="center">
                                    <Box mb={0.5}>
                                        <FontAwesomeIcon
                                            icon={faTrophyStar}
                                            color={theme.palette.success.main}
                                            size="6x"
                                        />
                                    </Box>
                                    <Typography color="muted.main" variant="h5"><strong>+{ctx.challengeRev.awardPoints}</strong></Typography>
                                    <Typography color="muted.main" variant="h5" textTransform="uppercase">{t("AssPlayer.Conclusion.Challenge.PointsText")}</Typography>
                                </Stack>
                            }
                        </Grid>
                    </Grid>
                    <Box sx={{
                        backgroundColor: theme.palette.notice.main,
                        borderRadius: "8px",
                        borderStyle: "solid",
                        borderWidth: "2px",
                        borderColor: theme.palette.muted.main
                    }}
                        padding={1}
                    >
                        <Stack direction="column" spacing={2}>
                            <Grid container spacing={2}>
                                <Grid>
                                    {
                                        ctx.challengeRev.topicThumbnailUrl &&
                                        <Avatar
                                            variant="rounded"
                                            sx={{ ...AppImageDimension.ThumbnailLg }}
                                            src={ctx.challengeRev.topicThumbnailUrl}
                                        />
                                    }
                                </Grid>
                                <Grid size="grow">
                                    <Typography
                                        variant="h6"
                                        fontSize="18px"
                                        fontWeight="700"
                                        textTransform="uppercase"
                                        color={theme.palette.text.primary}
                                    >
                                        {ctx.challengeRev.topicName}
                                    </Typography>
                                    {
                                        ctx.challengeRev.scorePercent < 100 && ctx.challengeRev.knowledgeTestsLeft > 0
                                            ?
                                            <>
                                                <Typography variant="body2">
                                                    <strong>
                                                        {t("AssPlayer.Conclusion.Challenge.KnowledgePercentText", { count: ctx.challengeRev.topicKnowledgePercent })}
                                                    </strong>
                                                </Typography>
                                                <Typography variant="caption">
                                                    {t(ctx.challengeRev.topicGrantSealProvenKnowledge
                                                        ? "AssPlayer.Conclusion.Challenge.KnowledgeTestsLeftSeal"
                                                        : "AssPlayer.Conclusion.Challenge.KnowledgeTestsLeft", { count: ctx.challengeRev.knowledgeTestsLeft })}
                                                </Typography>
                                            </>
                                            :
                                            <Grid container spacing={2} mt={1}>
                                                {
                                                    ctx.challengeRev.hasProvenKnowledgeSeal && ctx.challengeRev.topicGrantSealProvenKnowledge &&
                                                    <Grid>
                                                        <MprcText color="info">
                                                            <FontAwesomeIcon icon={faShieldQuartered} size="4x" />
                                                        </MprcText>
                                                    </Grid>
                                                }
                                                <Grid size="grow">
                                                    <Typography variant="body2" color="success.main">
                                                        <strong>{t("AssPlayer.Conclusion.Challenge.CongratulationsText")}</strong>
                                                    </Typography>
                                                    <Typography variant="caption" color="success.main">
                                                        {
                                                            ctx.challengeRev.challengeMode == ChallengeMode.Retention
                                                                ? t("AssPlayer.Conclusion.Challenge.RetentionText")
                                                                : ctx.challengeRev.hasProvenKnowledgeSeal && ctx.challengeRev.topicGrantSealProvenKnowledge
                                                                    ? t("AssPlayer.Conclusion.Challenge.KnowledgeSealText")
                                                                    : t("AssPlayer.Conclusion.Challenge.KnowledgeText")
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                    }
                                </Grid>
                            </Grid>
                            <Grid container alignItems="start" columnSpacing={2} marginX={1} width="98%">
                                <Grid size="grow">
                                    <MprcProgress
                                        value={ctx.challengeRev.topicKnowledgePercent}
                                        fullWidth
                                        color="primary"
                                        height={12}
                                        backgroundColor="#dadada"
                                        border="none"
                                    />
                                    <ProgressTable>
                                        <tr>
                                            <ProgressMarker><strong>0%</strong></ProgressMarker>
                                            <ProgressMarker><strong>25%</strong></ProgressMarker>
                                            <ProgressMarker><strong>50%</strong></ProgressMarker>
                                            <ProgressMarker>
                                                <div style={{ float: "left" }}><strong>75%</strong></div>
                                                <div style={{ float: "right" }}><strong>100%</strong></div>
                                            </ProgressMarker>
                                        </tr>
                                    </ProgressTable>
                                </Grid>
                                {
                                    ctx.challengeRev.topicGrantSealProvenKnowledge &&
                                    <Grid>
                                        <MprcText color="info">
                                            <FontAwesomeIcon icon={faShieldQuartered} size="2x" />
                                        </MprcText>
                                    </Grid>
                                }
                            </Grid>
                        </Stack>
                    </Box>
                </Stack>
            }
        </>
    );
};
